import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCheck } from "react-icons/fa";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import {
  areBuyerCmLogisticsStagesComplete,
  CM_BUYER_LOGISTICS_STAGES,
  findCurrentProductionStageId,
  isCmPipelineFullyComplete,
  isCmStageProgressCompleted,
  normalizeCmProgressMap,
  normalizeCmStageId,
  parseCmRfqIdParam,
  progressEntryForStage,
  resolveCmProductionStages,
  unwrapApiData,
} from "./cm/cmProductionPaths";
import "../../assets/styles/cm_production_workspace.scss";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";

const BUYER_STAGE_COPY = {
  accept_invoice: {
    title: "Accept invoice",
    body: "Review the seller invoice in your standard approval workspace, then mark this step complete.",
    cta: { label: "Go to approve invoices", to: "/approve-invoice-list" },
  },
  accept_manifest: {
    title: "Review shipping manifest",
    body: "Open your shipping manifest list, accept the shipment when details match, then complete this step.",
    cta: { label: "Shipping manifests", to: "/shipping-manifest-list" },
  },
  generate_grn: {
    title: "Create GRN",
    body: "Record goods receipt against the shipment using the standard GRN flow.",
    cta: { label: "Create GRN", to: "/buyer-goods-receipt-generate" },
  },
};

export default function BuyerCmProductionWorkspacePage() {
  const { rfqId: rfqIdParam } = useParams();
  const rfqId = parseCmRfqIdParam(rfqIdParam) || rfqIdParam;
  const [loading, setLoading] = useState(true);
  const [production, setProduction] = useState(null);
  const [fulfillment, setFulfillment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ppRes, fRes] = await Promise.all([
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.BUYER_PRODUCTION_PROCESS_GET(rfqId)),
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.BUYER_FULFILLMENT(rfqId)),
      ]);
      setProduction(unwrapApiData(ppRes));
      setFulfillment(unwrapApiData(fRes));
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load");
      setProduction(null);
      setFulfillment(null);
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    load();
  }, [load]);

  async function completeStage() {
    try {
      setSubmitting(true);
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.BUYER_PRODUCTION_PROCESS_COMPLETE_STAGE(rfqId), {});
      toast.success("Stage updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not complete stage");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="cm-prod-workspace container py-4">
        <p className="text-muted">Loading production workspace…</p>
      </div>
    );
  }

  const pp = production;
  if (!pp?.exists) {
    return (
      <div className="cm-prod-workspace container py-4">
        <div className="alert alert-warning">
          Production has not been started for this RFQ yet (manufacturer must configure stages first).
        </div>
        <Link to="/dashboard/buyer/contract-manufacturers/quotes" className="btn btn-outline-secondary">
          Back to CM quotes
        </Link>
      </div>
    );
  }

  const snap = fulfillment || {};
  const progress = normalizeCmProgressMap(pp.progress ?? pp.progress_json ?? pp.progressJson);
  const stages = resolveCmProductionStages(pp.stages ?? pp.stages_json ?? pp.stagesJson, progress);
  const currentId = findCurrentProductionStageId(stages, progress);
  const buyerStage = currentId && BUYER_STAGE_COPY[currentId] ? currentId : null;
  // "currentId === null" means every stage is completed per the same rule the pipeline list uses.
  // We keep a dedicated flag so the UI doesn't fall back to a "waiting" message on fully-complete pipelines.
  const pipelineCompletedByCurrentId = stages.length > 0 && !currentId;
  const hasBuyerLogisticsInPipeline = stages.some((sid) => CM_BUYER_LOGISTICS_STAGES.includes(normalizeCmStageId(sid)));
  const hasGenerateGrnInPipeline = stages.some((s) => normalizeCmStageId(s) === "generate_grn");
  const grnStatus = String(snap?.grn?.status ?? "").trim().toLowerCase();
  const grnRecorded =
    hasGenerateGrnInPipeline &&
    !!String(snap?.grn?.grnNumber ?? "").trim() &&
    grnStatus !== "draft";
  // Buyer receiving steps (only when those stages exist in this RFQ); GRN on PO can count as generate_grn done.
  const buyerReceivingStepsComplete = areBuyerCmLogisticsStagesComplete(stages, progress, {
    treatGenerateGrnCompleteIfGrn: grnRecorded,
  });
  // Delivery page when every pipeline row is completed (matches badges; tolerates camelCase progress keys).
  const deliveryPageAvailable = pipelineCompletedByCurrentId || isCmPipelineFullyComplete(stages, progress);

  return (
    <div className="cm-prod-workspace container py-4">
      <div className="cm-prod-workspace__hero mb-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-2">
            <li className="breadcrumb-item">
              <Link to="/dashboard/buyer">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/dashboard/buyer/contract-manufacturers/quotes">Contract manufacturing</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Production · RFQ #{rfqId}
            </li>
          </ol>
        </nav>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div>
            <h1 className="h3 mb-1">Production & logistics</h1>
            <p className="text-muted mb-0">
              Order reference: <strong>{snap.orderLabel || "—"}</strong>
              {snap.poNumber ? (
                <>
                  {" "}
                  · PO <strong>{snap.poNumber}</strong>
                </>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      <div className="cm-prod-workistics-panels mb-3">
        <LinkedLogisticsPanel
          sourceModule="contract_manufacturing"
          sourceRecordType="manufacturing_rfq"
          sourceRecordId={String(rfqId)}
          linkPurpose="raw_material_inward"
          title="Raw material inward"
        />
        <LinkedLogisticsPanel
          sourceModule="contract_manufacturing"
          sourceRecordType="manufacturing_sample_dispatch"
          sourceRecordId={`${rfqId}:sample`}
          linkPurpose="sample_dispatch"
          title="Sample dispatch logistics"
        />
        <LinkedLogisticsPanel
          sourceModule="contract_manufacturing"
          sourceRecordType="manufacturing_final_dispatch"
          sourceRecordId={`${rfqId}:final`}
          linkPurpose="final_dispatch"
          title="Final dispatch logistics"
        />
        <LinkedLogisticsPanel
          sourceModule="contract_manufacturing"
          sourceRecordType="manufacturing_recurring_dispatch"
          sourceRecordId={`${rfqId}:recurring`}
          linkPurpose="recurring_batch"
          title="Recurring batch logistics"
        />
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <div className="card shadow-sm border-0 cm-prod-workspace__card">
            <div className="card-body">
              <h2 className="h6 text-uppercase text-secondary mb-3">Pipeline</h2>
              <ul className="list-unstyled mb-0 cm-prod-workspace__pipeline">
                {stages.map((sid) => {
                  const row = progressEntryForStage(progress, sid);
                  const st = String(row?.status ?? "")
                    .trim()
                    .toLowerCase();
                  return (
                    <li key={sid} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                      <span className="text-capitalize">{String(sid).replace(/_/g, " ")}</span>
                      <span
                        className={`badge ${
                          isCmStageProgressCompleted(progress, sid)
                            ? "bg-success"
                            : st === "in_progress"
                              ? "bg-primary"
                              : "bg-secondary"
                        }`}
                      >
                        {row?.status || "pending"}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {deliveryPageAvailable ? (
                <div className="mt-4 pt-3 border-top">
                  <p className="text-muted small mb-2">Every pipeline stage is finished.</p>
                  <Link
                    to={`/dashboard/buyer/contract-manufacturers/production/${rfqId}/delivery-confirmation`}
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                  >
                    <FaCheck aria-hidden />
                    Open delivery page
                  </Link>
                </div>
              ) : null}
            </div>
          </div>

          {deliveryPageAvailable ? (
            <div className="mt-3">
              <div className="alert alert-success mb-0">
                All production stages are completed for this RFQ. Use the delivery page to review details and confirm
                acceptance when you are ready.
              </div>
            </div>
          ) : hasBuyerLogisticsInPipeline && buyerReceivingStepsComplete && !deliveryPageAvailable ? (
            <div className="mt-3">
              <div className="alert alert-info mb-0">
                Your receiving steps are complete. The delivery page will be available once every remaining pipeline
                stage is finished (including on the manufacturer side).
              </div>
            </div>
          ) : buyerStage ? (
            <div className="card shadow-sm border-0 mt-3 cm-prod-workspace__card cm-prod-workspace__card--accent">
              <div className="card-body">
                <h2 className="h5 mb-2">{BUYER_STAGE_COPY[buyerStage].title}</h2>
                <p className="text-muted">{BUYER_STAGE_COPY[buyerStage].body}</p>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Link className="btn btn-primary" to={BUYER_STAGE_COPY[buyerStage].cta.to}>
                    {BUYER_STAGE_COPY[buyerStage].cta.label}
                  </Link>
                  {pp.canCompleteCurrentStage ? (
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      disabled={submitting}
                      onClick={completeStage}
                    >
                      {submitting ? "Saving…" : "Mark step complete"}
                    </button>
                  ) : null}
                </div>
                {!pp.canCompleteCurrentStage ? (
                  <p className="small text-muted mt-2 mb-0">
                    Complete the required action in the main app first; this button unlocks when the step is active.
                  </p>
                ) : null}
              </div>
            </div>
          ) : currentId ? (
            <div className="alert alert-info mt-3 mb-0">
              <span className="fw-semibold d-block mb-1">Waiting on the manufacturer</span>
              The step{" "}
              <span className="text-capitalize">{String(currentId).replace(/_/g, " ")}</span> is completed in the
              contract manufacturer workspace. This view will refresh when the pipeline advances.
            </div>
          ) : pipelineCompletedByCurrentId ? (
            <div className="alert alert-success mt-3 mb-0">
              All production stages are completed for this RFQ. Use the delivery page to review details and confirm
              acceptance when you are ready.
            </div>
          ) : (
            <div className="alert alert-info mt-3 mb-0">
              Production status will update when the next step becomes active.
            </div>
          )}
        </div>

        <div className="col-lg-4">
          <div className="card shadow-sm border-0 cm-prod-workspace__card">
            <div className="card-body">
              <h2 className="h6 text-uppercase text-secondary mb-3">Documents</h2>
              <dl className="row small mb-0">
                <dt className="col-5">Invoice</dt>
                <dd className="col-7">{snap.invoice?.invoiceNumber || "—"}</dd>
                <dt className="col-5">E-way</dt>
                <dd className="col-7">{snap.ewayBill?.ewbNumber || "—"}</dd>
                <dt className="col-5">Manifest</dt>
                <dd className="col-7">{snap.shippingManifest?.manifestNumber || "—"}</dd>
                <dt className="col-5">GRN</dt>
                <dd className="col-7">{snap.grn?.grnNumber || "—"}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
